import {Info} from 'lucide-react'
import { useState } from 'react'

interface TooltipProps {
  content: string
  title?: string
}

export function Tooltip({ content, title }: TooltipProps) {
  const [visible, setVisible] = useState(false)
  return (
    <span className="relative inline-block">
      <button
        type="button"
        className="ml-1 p-0.5 rounded-full transition-colors hover:bg-amber-100 focus:outline-none"
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onFocus={() => setVisible(true)}
        onBlur={() => setVisible(false)}
        aria-label="Show agronomist note"
      >
        <Info size={14} className="text-amber-600" />
      </button>
      {visible && (
        <div
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 w-64 rounded-2xl p-3 shadow-lg border text-xs leading-relaxed"
          style={{ background: '#fffbef', borderColor: '#fde68a', color: '#78350f' }}
        >
          {title && <p className="font-bold mb-1">🌿 {title}</p>}
          <p>{content}</p>
        </div>
      )}
    </span>
  )
}

interface CalcCardProps {
  title: string
  value: string | number
  sub?: string
  icon?: string
  accent?: string
}

export function MetricCard({ title, value, sub, icon, accent = '#f59e0b' }: CalcCardProps) {
  return (
    <div
      className="rounded-2xl p-5 border shadow-sm"
      style={{ background: '#fff', borderColor: '#e8d9b0' }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-widest mb-1.5" style={{ color: '#a16207' }}>
            {title}
          </p>
          <p className="text-3xl font-bold leading-tight tracking-tight" style={{ color: '#1c1917' }}>
            {value}
          </p>
          {sub && <p className="text-sm mt-1" style={{ color: '#78716c' }}>{sub}</p>}
        </div>
        {icon && (
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 text-xl"
            style={{ background: accent + '20', border: `1.5px solid ${accent}50` }}
          >
            {icon}
          </div>
        )}
      </div>
    </div>
  )
}

interface SectionHeaderProps {
  h2: string
  description?: string
  badge?: string
}

export function SectionHeader({ h2, description, badge }: SectionHeaderProps) {
  return (
    <div className="mb-6">
      {badge && (
        <span
          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-3 border"
          style={{ background: '#fef3c7', borderColor: '#fbbf24', color: '#92400e' }}
        >
          {badge}
        </span>
      )}
      <h2 className="text-2xl font-bold tracking-tight" style={{ color: '#1c1917' }}>
        {h2}
      </h2>
      {description && (
        <p className="mt-1.5 text-sm leading-relaxed" style={{ color: '#78716c' }}>
          {description}
        </p>
      )}
    </div>
  )
}

interface RangeSliderProps {
  label: string
  value: number
  min: number
  max: number
  step?: number
  unit?: string
  onChange: (v: number) => void
  tooltip?: string
  formatValue?: (v: number) => string
}

export function RangeSlider({ label, value, min, max, step = 1, unit = '', onChange, tooltip, formatValue }: RangeSliderProps) {
  const pct = ((value - min) / (max - min)) * 100
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-sm font-semibold flex items-center" style={{ color: '#44403c' }}>
          {label}
          {tooltip && <Tooltip content={tooltip} />}
        </label>
        <span
          className="text-sm font-bold px-2.5 py-0.5 rounded-full"
          style={{ background: '#fef3c7', color: '#92400e' }}
        >
          {formatValue ? formatValue(value) : value}{unit}
        </span>
      </div>
      <div className="relative">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="w-full h-2 rounded-full appearance-none cursor-pointer"
          style={{
            background: `linear-gradient(to right, #d97706 0%, #d97706 ${pct}%, #e8d9b0 ${pct}%, #e8d9b0 100%)`,
          }}
        />
      </div>
      <div className="flex justify-between text-xs mt-1" style={{ color: '#a8a29e' }}>
        <span>{min}{unit}</span>
        <span>{max}{unit}</span>
      </div>
    </div>
  )
}

interface SelectFieldProps {
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (v: string) => void
  tooltip?: string
}

export function SelectField({ label, value, options, onChange, tooltip }: SelectFieldProps) {
  return (
    <div className="mb-4">
      <label className="text-sm font-semibold flex items-center mb-1.5" style={{ color: '#44403c' }}>
        {label}
        {tooltip && <Tooltip content={tooltip} />}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 appearance-none cursor-pointer"
        style={{ background: '#fff', borderColor: '#d6c89a', color: '#1c1917' }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  )
}

interface NumberFieldProps {
  label: string
  value: number
  min?: number
  step?: number
  unit?: string
  onChange: (v: number) => void
  tooltip?: string
}

export function NumberField({ label, value, min = 0, step = 1, unit, onChange, tooltip }: NumberFieldProps) {
  return (
    <div className="mb-4">
      <label className="text-sm font-semibold flex items-center mb-1.5" style={{ color: '#44403c' }}>
        {label}
        {tooltip && <Tooltip content={tooltip} />}
      </label>
      <div className="relative flex items-center">
        <input
          type="number"
          value={value}
          min={min}
          step={step}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          className="w-full px-3 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
          style={{ background: '#fff', borderColor: '#d6c89a', color: '#1c1917' }}
        />
        {unit && (
          <span
            className="absolute right-3 text-sm font-medium pointer-events-none"
            style={{ color: '#a16207' }}
          >
            {unit}
          </span>
        )}
      </div>
    </div>
  )
}

interface ToggleProps {
  options: [string, string]
  value: string
  onChange: (v: string) => void
}

export function Toggle({ options, value, onChange }: ToggleProps) {
  return (
    <div
      className="flex items-center rounded-xl p-1 border"
      style={{ background: '#fef9c3', borderColor: '#fde68a' }}
    >
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-200`}
          style={
            value === opt
              ? { background: '#d97706', color: '#fff' }
              : { color: '#92400e' }
          }
        >
          {opt}
        </button>
      ))}
    </div>
  )
}

interface AgronomistNoteProps {
  text: string
  type?: 'info' | 'warning' | 'success'
}

export function AgronomistNote({ text, type = 'info' }: AgronomistNoteProps) {
  const styles = {
    info: { bg: '#eff6ff', border: '#93c5fd', text: '#1e40af', icon: '💡' },
    warning: { bg: '#fff7ed', border: '#fdba74', text: '#9a3412', icon: '⚠️' },
    success: { bg: '#f0fdf4', border: '#86efac', text: '#14532d', icon: '✅' },
  }
  const s = styles[type]
  return (
    <div
      className="rounded-2xl p-4 border text-sm leading-relaxed mt-4"
      style={{ background: s.bg, borderColor: s.border, color: s.text }}
    >
      <span className="font-bold">{s.icon} Agronomist's Note: </span>
      {text}
    </div>
  )
}

interface CircularMeterProps {
  value: number
  max: number
  label: string
  unit?: string
  color?: string
}

export function CircularMeter({ value, max, label, unit = 'g', color = '#d97706' }: CircularMeterProps) {
  const pct = Math.min(value / max, 1)
  const r = 36
  const circ = 2 * Math.PI * r
  const dash = pct * circ
  return (
    <div className="flex flex-col items-center gap-1">
      <svg width="100" height="100" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r={r} fill="none" stroke="#e8d9b0" strokeWidth="8" />
        <circle
          cx="50" cy="50" r={r}
          fill="none"
          stroke={color}
          strokeWidth="8"
          strokeDasharray={`${dash} ${circ}`}
          strokeDashoffset={circ * 0.25}
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 0.5s ease' }}
        />
        <text x="50" y="48" textAnchor="middle" dominantBaseline="middle" fontSize="13" fontWeight="700" fill="#1c1917">
          {value.toFixed(0)}
        </text>
        <text x="50" y="62" textAnchor="middle" fontSize="9" fill="#78716c">{unit}</text>
      </svg>
      <span className="text-xs font-semibold text-center" style={{ color: '#44403c' }}>{label}</span>
    </div>
  )
}
